import { groq } from 'next-sanity';
import { z } from 'zod';

import { basicText, SanityBasicText } from './basicText';
import { documentFootnotes, SanityDocumentFootnotes } from './footnotes';
import { form, SanityForm } from './form';
import { formConfirmation, SanityFormConfirmation } from './formConfirmation';
import { heroCentered, SanityHeroCentered } from './heroCentered';
import { heroForm, SanityHeroForm } from './heroForm';
import { heroSplit, SanityHeroSplit } from './heroSplit';
import { image, SanityImage } from './image';
import { link, SanityLink } from './link';
import { media } from './media';
import { relatedContent, SanityRelatedContent } from './relatedContent';
import { richAccordion, SanityRichAccordion } from './richAccordion';
import { richTextSection, SanityRichTextSection } from './richTextSection';
import { SanityAnchorPoint, SanityCalculatorSection } from './sections';
import { SanityStats, stats } from './stats';
import {
  SanityTestimonialCarousel,
  testimonialCarousel,
} from './testimonialCarousel';
import {
  SanityTestimonialSection,
  testimonialSection,
} from './testimonialSection';
import { SanityThreeUp, threeUp } from './threeUp';
import { SanityTwoUp, twoUp } from './twoUp';
import { SanityTwoUpForm, twoUpForm } from './twoUpForm';

export const SanityResource = z.object({
  _id: z.string(),
  title: z.string(),
  slug: z.string(),
  href: z.string(),
  excerpt: z.string(),
  description: z.string(),
  publishedAt: z.string(),
  readTime: z.number(),
  category: z.object({
    title: z.string(),
    pluralTitle: z.string(),
    href: z.string(),
  }),
  introduction: SanityBasicText,
  heroButton: SanityLink.nullable(),
  heroMedia: SanityImage.nullable(),
  hasHeroForm: z.boolean(),
  hideHeroSection: z.boolean(),
  hideShareButtons: z.boolean(),
  heroForm: SanityForm.nullable(),
  heroFormConfirmation: SanityFormConfirmation.nullable(),
  shareLabel: z.string(),
  sections: z.array(
    z.union([
      SanityHeroCentered,
      SanityHeroSplit,
      SanityRichTextSection.omit({ sectionIndex: true }),
      SanityCalculatorSection,
      SanityTwoUpForm.omit({ sectionIndex: true }),
      SanityHeroForm,
      SanityTestimonialSection,
      SanityTestimonialCarousel,
      SanityTwoUp,
      SanityThreeUp,
      SanityRichAccordion,
      SanityStats,
      SanityAnchorPoint.omit({ sectionIndex: true }),
    ]),
  ),
  relatedContent: SanityRelatedContent.nullable(),
  footnotes: SanityDocumentFootnotes.optional().nullable(),
  thumbnailImage: SanityImage,
});
export type SanityResource = z.infer<typeof SanityResource>;

export const resource = groq`*[_type == 'resource' && slug.current == $slug][0] {
  _id,
  title,
  'slug': slug.current,
  'href': '/resources/' + slug.current,
  excerpt,
  "description": excerpt,
  publishedAt,
  readTime,
  category-> {
    title,
    pluralTitle,
    'href': '/resources/categories/' + slug.current,
  },
  introduction[] ${basicText},
  heroButton[0] ${link},
  hasHeroForm,
  'hideHeroSection': coalesce(hideHeroSection, false),
  'hideShareButtons': coalesce(hideShareButtons, false),
  heroForm-> ${form},
  heroFormConfirmation ${formConfirmation},
  heroMedia[0] ${media},
  shareLabel,
  sections[] {
    _type,
    _key,
    _type == 'calculator' => {
      sections[] {
        _type,
        _key,
        _type == 'sectionHeader' => {
          title,
          description[] ${basicText},
        },
        _type == 'field' => {
          variable,
          label,
          defaultValue,
        },
        _type == 'results' => {
          title,
          results[] {
            _key,
            label,
            calculation,
            format,
          },
          'background': background->color.hex,
        },
        _type == 'calculatorNote' => {
          // Drop broken icons (image object with no asset) so Zod optional
          // SanityImage doesn't reject the whole calculator section.
          'icon': select(defined(icon.asset) => icon ${image}, null),
          text,
          'background': background->color.hex,
        },
      },
      calculations[] {
        _key,
        variable,
        label,
        equation,
      }
    },
    _type == 'heroCentered' => ${heroCentered},
    _type == 'heroSplit' => ${heroSplit},
    _type == 'richTextSection' => ${richTextSection},
    _type == 'twoUpForm' => ${twoUpForm},
    _type == 'heroForm' => ${heroForm},
    _type == 'testimonialSection' => ${testimonialSection},
    _type == 'testimonialCarousel' => ${testimonialCarousel},
    _type == 'twoUp' => ${twoUp},
    _type == 'threeUp' => ${threeUp},
    _type == 'richAccordion' => ${richAccordion},
    _type == 'stats' => ${stats},
    _type == 'anchorPoint' => {
      'id': id.current,
    },
  },
  relatedContent ${relatedContent},
  footnotes ${documentFootnotes},
  thumbnailImage ${image},
}`;
